import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  AdminUser,
  AdminUserListResponse,
  AuthorizeUserPayload,
  CreateUserPayload,
  DeleteUserPayload,
  LoadUsersPayload,
} from "./types";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

interface MutationState {
  saving: boolean;
  error: boolean;
  pendingId: number | null;
}

interface SaveState {
  saving: boolean;
  error: boolean;
}

export interface AdminDashboardState {
  list: AsyncState<AdminUserListResponse>;
  authorize: MutationState;
  delete: MutationState;
  create: SaveState;
}

export const initialState: AdminDashboardState = {
  list: { data: null, loading: false, error: false },
  authorize: { saving: false, error: false, pendingId: null },
  delete: { saving: false, error: false, pendingId: null },
  create: { saving: false, error: false },
};

export const adminDashboardSlice = createSlice({
  name: "adminDashboard",
  initialState,
  reducers: {
    loadUsers(state, _action: PayloadAction<LoadUsersPayload | undefined>) {
      state.list.loading = true;
      state.list.error = false;
    },
    usersLoaded(state, { payload }: PayloadAction<AdminUserListResponse>) {
      state.list.data = payload;
      state.list.loading = false;
      state.list.error = false;
    },
    loadUsersError(state) {
      state.list.loading = false;
      state.list.error = true;
    },

    authorizeUser(state, { payload }: PayloadAction<AuthorizeUserPayload>) {
      state.authorize.saving = true;
      state.authorize.error = false;
      state.authorize.pendingId = payload.userId;
    },
    userAuthorized(state, { payload }: PayloadAction<AdminUser>) {
      state.authorize.saving = false;
      state.authorize.error = false;
      state.authorize.pendingId = null;
      if (state.list.data) {
        state.list.data.users = state.list.data.users.map((u) =>
          u.id === payload.id ? payload : u,
        );
        state.list.data.pending_count = state.list.data.users.filter(
          (u) => u.status === "pending",
        ).length;
      }
    },
    authorizeUserError(state) {
      state.authorize.saving = false;
      state.authorize.error = true;
      state.authorize.pendingId = null;
    },

    deleteUser(state, { payload }: PayloadAction<DeleteUserPayload>) {
      state.delete.saving = true;
      state.delete.error = false;
      state.delete.pendingId = payload.userId;
    },
    userDeleted(state, { payload }: PayloadAction<DeleteUserPayload>) {
      state.delete.saving = false;
      state.delete.error = false;
      state.delete.pendingId = null;
      if (state.list.data) {
        const removed = state.list.data.users.find(
          (u) => u.id === payload.userId,
        );
        state.list.data.users = state.list.data.users.filter(
          (u) => u.id !== payload.userId,
        );
        state.list.data.total = Math.max(0, state.list.data.total - 1);
        if (removed?.status === "pending") {
          state.list.data.pending_count = Math.max(
            0,
            state.list.data.pending_count - 1,
          );
        }
      }
    },
    deleteUserError(state) {
      state.delete.saving = false;
      state.delete.error = true;
      state.delete.pendingId = null;
    },

    createUser(state, _action: PayloadAction<CreateUserPayload>) {
      state.create.saving = true;
      state.create.error = false;
    },
    userCreated(state, { payload }: PayloadAction<AdminUser>) {
      state.create.saving = false;
      state.create.error = false;
      if (state.list.data) {
        state.list.data.users = [payload, ...state.list.data.users];
        state.list.data.total += 1;
        if (payload.status === "pending") {
          state.list.data.pending_count += 1;
        }
      }
    },
    createUserError(state) {
      state.create.saving = false;
      state.create.error = true;
    },
  },
});

export const {
  loadUsers,
  usersLoaded,
  loadUsersError,
  authorizeUser,
  userAuthorized,
  authorizeUserError,
  deleteUser,
  userDeleted,
  deleteUserError,
  createUser,
  userCreated,
  createUserError,
} = adminDashboardSlice.actions;

export const { reducer } = adminDashboardSlice;
